// src/components/MyClipsPlayer.js
// MyClips Faz 1A Görev 5: HLS (hls.js) tabanlı video player.
//
// Amaç:
//  - Transcoder çıktı `manifestUrl` (master.m3u8) oynat
//  - Safari'de native HLS kullan (hls.js yok)
//  - Diğer tarayıcılarda hls.js ile MSE
//  - Legacy MP4 clipleri de oynat (manifestUrl yoksa fallbackUrl)
//  - Poster (posterUrl) ilk kare boşluğunu kapatır
//  - Görünürlük bazlı autoplay (IntersectionObserver) — VEYA dışarıdan `active` prop
//  - preload="none" | "metadata" | "auto" — liste içinde N/N+1/N-1 stratejisi için
//
// Kullanım:
//   <MyClipsPlayer
//     manifestUrl={clip.manifestUrl}
//     posterUrl={clip.posterUrl}
//     fallbackUrl={clip.mediaUrl}   // legacy MP4
//     active={true}                   // null => IO ile otomatik
//     muted
//     preload="auto"
//   />
//
// N/N+1/N-1 stratejisi (parent'ta uygulanmalı):
//   - Aktif clip: preload="auto" + active=true
//   - Komşu (±1) clipler: preload="metadata" + active=false  (manifest/init segmenti cache'lenir)
//   - Uzak clipler: preload="none" + active=false           (hls instance hiç kurulmaz)

import {
  useEffect,
  useRef,
  useState,
  useCallback,
  type CSSProperties,
  type MouseEventHandler,
  type ReactEventHandler,
  type SyntheticEvent,
} from "react";
import "./MyClipsPlayer.css";
import { reportClipView, reportClipComplete } from "../services/clipViews";

/** hls.js kurucusu — paketin KENDİ `dist/hls.d.ts`inden TÜRETİLDİ, elle
 *  yazılmadı. `import(...)` tip konumunda kullanıldığı için derlemede silinir;
 *  lazy-chunk bölünmesi (Safari native HLS'de hiç indirilmemesi) bozulmaz. */
type HlsCtor = typeof import("hls.js").default;
/** Kurucudan türetilen örnek tipi (`hlsRef` + yerel `hlsInstance`). */
type HlsInstance = InstanceType<HlsCtor>;

// hls.js'i lazy import et (ilk manifest çağrısında yüklenir).
// Webpack dynamic import chunk'a böler — Safari native HLS'de hiç indirilmez.
let _hlsModulePromise: Promise<HlsCtor> | null = null;
function loadHlsModule(): Promise<HlsCtor> {
  if (!_hlsModulePromise) {
    _hlsModulePromise = import(/* webpackChunkName: "hls-js" */ "hls.js").then(
      // `m.default || m` = CJS/ESM interop yedeği; bazı bundler konfiglerinde
      // modül nesnesinin KENDİSİ kurucudur. İki şekil tek statik tipte
      // ifade edilemiyor → davranış AYNEN korunup sonuç tek noktada
      // daraltılıyor (gövde değişmedi, yalnız iddia eklendi).
      (m) => (m.default || m) as HlsCtor
    );
  }
  return _hlsModulePromise;
}

function isNativeHls(video: HTMLVideoElement | null) {
  if (!video) return false;
  return (
    video.canPlayType("application/vnd.apple.mpegurl") !== "" ||
    video.canPlayType("application/x-mpegURL") !== ""
  );
}

function isHlsUrl(url?: string) {
  if (!url) return false;
  // basit uç: .m3u8 uzantısı veya query
  return /\.m3u8(\?|$)/i.test(url);
}

/** `onReady` yükü — gövde iki yerde `{ native: true }`, bir yerde
 *  `{ native: false }` gönderiyor; başka alan yok. */
type ReadyInfo = { native: boolean };

/** ⚠️ Bu tipteki ONBEŞ alanın hepsi ÖLÇÜLEREK daraltıldı: dokuz tanesi
 *  `any`di (`clipId`/`manifestUrl`/`posterUrl`/`fallbackUrl`/`active` +
 *  dört callback). Onbir çağrı yerinin dokuzu prop'ları
 *  `{...({…} as any)}` ile yayıyor, ikisi (`MyClips.tsx:1008` ve `:1031`)
 *  doğrudan veriyor — daraltma o iki yere karşı ölçüldü.
 *  URL alanları `string`: üreticiler Firestore klip doc'undan
 *  `manifestUrl`/`posterUrl`/`mediaUrl`/`videoUrl` okuyor ve gövde bunları
 *  yalnız `||` zinciri, regex testi ve `video.src` ataması olarak kullanıyor. */
type MyClipsPlayerProps = {
  clipId?: string;
  manifestUrl?: string;
  posterUrl?: string;
  fallbackUrl?: string;
  /** `null` = "IntersectionObserver'a bırak" (varsayılan); `boolean` = zorla.
   *  Gövdedeki `active !== null` dalı tam olarak bu üç durumu ayırıyor. */
  active?: boolean | null;
  muted?: boolean;
  loop?: boolean;
  playsInline?: boolean;
  /** Dosya başlığındaki N/N+1/N-1 stratejisinin üç değeri — gövde
   *  `preload === "none"` ve `preload === "auto"` ile ikisini ayrıca sınıyor. */
  preload?: "none" | "metadata" | "auto";
  controls?: boolean;
  className?: string;
  style?: CSSProperties;
  onClick?: MouseEventHandler<HTMLDivElement>;
  onReady?: (info: ReadyInfo) => void;
  /** İki farklı kaynaktan çağrılıyor: hls.js `ErrorData`sı ve `catch (err)`
   *  → ortak üst sınır `unknown`; tüketici daraltmayı kendi yapar. */
  onError?: (err: unknown) => void;
  onEnded?: ReactEventHandler<HTMLVideoElement>;
  intersectionThreshold?: number;
};

export default function MyClipsPlayer({
  clipId,                  // Faz 1B: view telemetry (null ise telemetri atlanır)
  manifestUrl,
  posterUrl,
  fallbackUrl,
  active = null,           // true/false => zorla; null => IO ile
  muted = true,
  loop = true,
  playsInline = true,
  preload = "metadata",    // "none" | "metadata" | "auto"
  controls = false,
  className = "",
  style,
  onClick,
  onReady,
  onError,
  onEnded,
  intersectionThreshold = 0.8,
}: MyClipsPlayerProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<HlsInstance | null>(null);
  const [visible, setVisible] = useState(active ?? false);
  const [hasError, setHasError] = useState(false);

  // ── Faz 1B: view telemetry session bayrakları ──
  // Bu player instance için telemetri bir kez gönderilir. clipId değişirse reset.
  const telemetryRef = useRef<{
    clipId: string | null;
    viewStartAt: number;
    viewSent: boolean;
    completeSent: boolean;
  }>({
    clipId: null,
    viewStartAt: 0,   // "playing" event ts (ms) — 2s dwell ölçümü
    viewSent: false,  // reportClipView gönderildi mi?
    completeSent: false,
  });

  // "play etmeli mi" bayrağı: dış prop varsa onu kullan, yoksa IO'dan gelen visible
  const shouldPlay = active !== null ? active : visible;

  // Hangi kaynağı oynatacağız?
  // manifestUrl varsa HLS; yoksa fallbackUrl (legacy MP4) kullanılır.
  const srcToUse = manifestUrl || fallbackUrl || "";
  const useHls = !!manifestUrl && isHlsUrl(manifestUrl);

  // ── IntersectionObserver (yalnızca active=null ise) ──
  useEffect(() => {
    if (active !== null) return;
    const el = videoRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting),
      { threshold: intersectionThreshold }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [active, intersectionThreshold]);

  // ── HLS/src kurulumu ──
  useEffect(() => {
    setHasError(false);
    const video = videoRef.current;
    if (!video || !srcToUse) return;

    // preload="none" ve henüz oynatmaya çalışmıyoruz → hiçbir şey indirme
    if (preload === "none" && !shouldPlay) {
      // Native src'i de sıfırla (tarayıcı metadata çekmesin)
      if (video.src) {
        try {
          video.removeAttribute("src");
          video.load();
        } catch {}
      }
      if (hlsRef.current) {
        try { hlsRef.current.destroy(); } catch {}
        hlsRef.current = null;
      }
      return;
    }

    // Non-HLS (MP4 legacy) → direkt src
    if (!useHls) {
      if (video.src !== srcToUse) {
        video.src = srcToUse;
      }
      onReady && onReady({ native: true });
      return () => {};
    }

    // HLS — Safari native destek varsa hls.js yükleme, direkt video.src
    if (isNativeHls(video)) {
      if (video.src !== srcToUse) {
        video.src = srcToUse;
      }
      onReady && onReady({ native: true });
      return () => {};
    }

    // HLS + hls.js
    let cancelled = false;
    let hlsInstance: HlsInstance | null = null;

    (async () => {
      try {
        const Hls = await loadHlsModule();
        if (cancelled) return;
        if (!Hls.isSupported()) {
          // Son çare: yine de native dene
          video.src = srcToUse;
          onReady && onReady({ native: true });
          return;
        }

        // Önceki instance varsa temizle
        if (hlsRef.current) {
          try { hlsRef.current.destroy(); } catch {}
          hlsRef.current = null;
        }

        const isActivePreload = preload === "auto";
        hlsInstance = new Hls({
          enableWorker: true,
          lowLatencyMode: false,
          backBufferLength: 0,
          maxBufferLength: isActivePreload ? 30 : 3,
          maxMaxBufferLength: isActivePreload ? 60 : 6,
          startLevel: isActivePreload ? -1 : 0,
          capLevelToPlayerSize: true,
          abrEwmaDefaultEstimate: isActivePreload ? 1_500_000 : 500_000,
        });

        hlsRef.current = hlsInstance;

        // Aşağıdaki üç `hlsInstance!`, BU SATIRDAKİ kayda atıftır: handler
        // `hlsInstance`in KENDİSİNE, atamasından hemen sonra bağlanıyor —
        // yani örnek yokken tetiklenemez. TS kapanış (closure) içinde dış
        // atamayı daraltamadığı için iddia gerekiyor; `!` yalnız derleme
        // zamanında yaşar, üretilen JS ve mevcut `try/catch`ler AYNEN kalır.
        hlsInstance.on(Hls.Events.ERROR, (_evt, data) => {
          if (cancelled) return;
          if (data?.fatal) {
            console.warn("[MyClipsPlayer] fatal HLS error:", data.type, data.details);
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                try { hlsInstance!.startLoad(); } catch {}
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                try { hlsInstance!.recoverMediaError(); } catch {}
                break;
              default:
                setHasError(true);
                try { hlsInstance!.destroy(); } catch {}
                hlsRef.current = null;
                onError && onError(data);
                break;
            }
          }
        });

        hlsInstance.on(Hls.Events.MANIFEST_PARSED, () => {
          if (!cancelled) onReady && onReady({ native: false });
        });

        hlsInstance.loadSource(srcToUse);
        hlsInstance.attachMedia(video);
      } catch (err) {
        console.error("[MyClipsPlayer] HLS setup hatası:", err);
        if (!cancelled) {
          setHasError(true);
          onError && onError(err);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (hlsInstance) {
        try { hlsInstance.destroy(); } catch {}
      }
      if (hlsRef.current === hlsInstance) hlsRef.current = null;
    };
  // srcToUse / preload / shouldPlay değişiminde tekrar kur (preload=none→auto geçişleri için)
  // onReady/onError bilerek dep listesine eklemedik (yeniden kurma yapmasın)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [srcToUse, useHls, preload, shouldPlay]);

  // ── Play/Pause senkronu ──
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (shouldPlay) {
      const p = video.play();
      if (p && typeof p.catch === "function") {
        p.catch(() => {
          // Autoplay politikası veya user-gesture eksik — sessizce yut
        });
      }
    } else {
      try { video.pause(); } catch {}
    }
  }, [shouldPlay]);

  // ── Dışarıdan muted değiştiğinde senkronla ──
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = muted;
  }, [muted]);

  // ── Unmount temizlik ──
  useEffect(() => {
    return () => {
      if (hlsRef.current) {
        try { hlsRef.current.destroy(); } catch {}
        hlsRef.current = null;
      }
    };
  }, []);

  // ── Faz 1B: izlenme telemetrisi ──
  // clipId değiştiğinde session bayraklarını resetle.
  useEffect(() => {
    telemetryRef.current = {
      clipId: clipId || null,
      viewStartAt: 0,
      viewSent: false,
      completeSent: false,
    };
  }, [clipId]);

  // Player event'lerine bağlanıp dwell (2s) ve %90 eşiklerini izle.
  useEffect(() => {
    if (!clipId) return; // telemetri opsiyonel
    const video = videoRef.current;
    if (!video) return;

    const VIEW_DWELL_MS = 2000;     // ≥2s izlenirse "view" say
    const COMPLETE_RATIO = 0.9;     // ≥%90 izlenirse "complete" say

    const onPlaying = () => {
      const t = telemetryRef.current;
      if (!t || t.clipId !== clipId) return;
      if (t.viewStartAt === 0) {
        t.viewStartAt = Date.now();
      }
    };

    const onTimeUpdate = () => {
      const t = telemetryRef.current;
      if (!t || t.clipId !== clipId) return;

      // 2s dwell'i geçince view raporla
      if (!t.viewSent && t.viewStartAt > 0) {
        const dwell = Date.now() - t.viewStartAt;
        if (dwell >= VIEW_DWELL_MS) {
          t.viewSent = true;
          const watchedMs = Math.max(0, Math.floor((video.currentTime || 0) * 1000));
          reportClipView(clipId, watchedMs);
        }
      }

      // %90 eşiği geçince complete raporla
      if (!t.completeSent) {
        const dur = Number(video.duration) || 0;
        const cur = Number(video.currentTime) || 0;
        if (dur > 0 && cur / dur >= COMPLETE_RATIO) {
          t.completeSent = true;
          const watchedMs = Math.max(0, Math.floor(cur * 1000));
          reportClipComplete(clipId, watchedMs);
        }
      }
    };

    const onVideoEnded = () => {
      const t = telemetryRef.current;
      if (!t || t.clipId !== clipId) return;
      // Doğal bitiş = %100 izlendi
      if (!t.completeSent) {
        t.completeSent = true;
        const watchedMs = Math.max(0, Math.floor((video.duration || 0) * 1000));
        reportClipComplete(clipId, watchedMs);
      }
    };

    const onPauseOrHidden = () => {
      // Playing event'i kesildiğinde dwell timer'ı sıfırla — sonra resume'da
      // yeni play'den itibaren 2s beklensin. viewSent zaten true ise no-op.
      const t = telemetryRef.current;
      if (!t || t.clipId !== clipId) return;
      if (!t.viewSent) t.viewStartAt = 0;
    };

    video.addEventListener("playing", onPlaying);
    video.addEventListener("timeupdate", onTimeUpdate);
    video.addEventListener("ended", onVideoEnded);
    video.addEventListener("pause", onPauseOrHidden);

    return () => {
      video.removeEventListener("playing", onPlaying);
      video.removeEventListener("timeupdate", onTimeUpdate);
      video.removeEventListener("ended", onVideoEnded);
      video.removeEventListener("pause", onPauseOrHidden);
    };
  }, [clipId]);

  // ── HLS.js ile loop fix ──
  // HLS.js kullanıldığında <video loop> attribute çalışmıyor:
  // ended event firlıyor ama HLS.js buffer'dan tekrar başlamak yerine duruyor.
  // Çözüm: ended event'inde manuel currentTime=0 + play().
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const handleLoopEnded = () => {
      if (!loop) return;
      try {
        video.currentTime = 0;
        const p = video.play();
        if (p && typeof p.catch === "function") {
          p.catch(() => {});
        }
      } catch {}
    };
    video.addEventListener("ended", handleLoopEnded);
    return () => video.removeEventListener("ended", handleLoopEnded);
  }, [loop]);

  const handleEnded = useCallback(
    // `<video onEnded>`in olayı; `onEnded` prop'u da aynı tipi bekliyor
    // (`ReactEventHandler<HTMLVideoElement>`), yani ikisi tek şekle bağlı.
    (e: SyntheticEvent<HTMLVideoElement>) => {
      onEnded && onEnded(e);
    },
    [onEnded]
  );

  return (
    <div
      className={`mycp-wrap ${className}`.trim()}
      style={style}
      onClick={onClick}
      data-state={hasError ? "error" : shouldPlay ? "playing" : "paused"}
    >
      <video
        ref={videoRef}
        className="mycp-video"
        poster={posterUrl || undefined}
        muted={muted}
        loop={loop}
        playsInline={playsInline}
        controls={controls}
        preload={preload}
        onEnded={handleEnded}
        // `src` sadece non-HLS / native-HLS yolunda set edilir (useEffect içinde)
      />
      {hasError && (
        <div className="mycp-error" role="status" aria-live="polite">
          Video yüklenemedi
        </div>
      )}
    </div>
  );
}

